/**
 * useDockablePanel — state + localStorage persistence for DockablePanel.
 *
 * Manages panel mode ('floating' | 'docked'), docked width, and exposes
 * a computed bodyPadding so the parent page can offset its content.
 */
import { useState, useCallback, useEffect } from 'react';

const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;

function readStorage(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota / private-mode */ }
}

export default function useDockablePanel(storageKey = 'fg_dockable_panel') {
  const modeKey  = `${storageKey}_mode`;
  const widthKey = `${storageKey}_width`;

  const [mode, _setMode]   = useState(() => readStorage(modeKey, 'floating'));
  const [width, _setWidth] = useState(() => {
    const w = readStorage(widthKey, DEFAULT_WIDTH);
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w));
  });

  const setMode = useCallback((m) => {
    _setMode(m);
    writeStorage(modeKey, m);
  }, [modeKey]);

  const setWidth = useCallback((w) => {
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w));
    _setWidth(clamped);
    writeStorage(widthKey, clamped);
  }, [widthKey]);

  // Force floating on narrow viewports
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e) => { if (e.matches && mode === 'docked') setMode('floating'); };
    handler(mql);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mode, setMode]);

  const docked = mode === 'docked';
  const bodyPadding = docked ? width : 0;

  return { mode, setMode, width, setWidth, docked, bodyPadding };
}

export { DEFAULT_WIDTH, MIN_WIDTH, MAX_WIDTH };
